import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Calendar, AlertCircle } from "lucide-react";
import type { College, Candidate } from "@/types/database";
import { useServerTime } from "@/hooks/useServerTime";
import {
  getServerTimeIST,
  formatDisplayDate,
  formatDisplayTime,
  formatISTDate,
  formatISTTime,
} from "@/utils/timezoneUtils";

interface TimeValidationGuardProps {
  children: React.ReactNode;
  college: College | null;
  candidate: Candidate | null;
  onTimeExpired?: () => void; // New prop for auto-submission on time expiration
}

interface ValidationResult {
  isValid: boolean;
  message: string;
  icon: typeof Clock | typeof Calendar | typeof AlertCircle;
  iconColor: string;
  titleColor: string;
  isTimeExpired?: boolean; // New flag to indicate if expiration is due to end_time
}

function validateAssessmentTiming(
  college: College | null,
  candidate: Candidate | null,
  serverTimeOffset: number
): ValidationResult {
  if (
    !college ||
    !college.college_visit_date ||
    !college.start_time ||
    !college.end_time
  ) {
    return {
      isValid: false,
      message: "Assessment schedule not configured for this college.",
      icon: AlertCircle,
      iconColor: "text-orange-500",
      titleColor: "text-orange-600",
    };
  }

  // Use server time instead of client time
  const serverTime = getServerTimeIST(serverTimeOffset);
  const currentDate = formatISTDate(serverTime); // YYYY-MM-DD format in IST
  const currentTime = formatISTTime(serverTime); // HH:MM:SS format in IST

  if (candidate?.assesment_started_at && candidate.assessment_ends_at) {
    const assessmentEndsAt = new Date(candidate.assessment_ends_at);

    if (serverTime > assessmentEndsAt) {
      return {
        isValid: false,
        message: "Your assessment time has ended.",
        icon: Clock,
        iconColor: "text-red-500",
        titleColor: "text-red-600",
        isTimeExpired: true,
      };
    }

    return {
      isValid: true,
      message: "Assessment is available now.",
      icon: Clock,
      iconColor: "text-green-500",
      titleColor: "text-green-600",
    };
  }

  // Check if current date matches college visit date
  if (currentDate !== college.college_visit_date) {
    const visitDate = formatDisplayDate(college.college_visit_date);
    return {
      isValid: false,
      message: `Assessment is only available on ${visitDate}.`,
      icon: Calendar,
      iconColor: "text-orange-500",
      titleColor: "text-orange-600",
    };
  }

  // Check if current time is before start time
  if (currentTime < college.start_time) {
    return {
      isValid: false,
      message: `Assessment starts at ${formatDisplayTime(college.start_time)}. Please wait until the assessment window opens.`,
      icon: Clock,
      iconColor: "text-orange-500",
      titleColor: "text-orange-600",
    };
  }

  // Check if current time is after end time
  if (currentTime > college.end_time) {
    return {
      isValid: false,
      message: `Assessment window has closed. The assessment ended at ${formatDisplayTime(college.end_time)}.`,
      icon: Clock,
      iconColor: "text-red-500",
      titleColor: "text-red-600",
      isTimeExpired: true, // Set flag for time expiration
    };
  }

  return {
    isValid: true,
    message: "Assessment is available now.",
    icon: Clock,
    iconColor: "text-green-500",
    titleColor: "text-green-600",
  };
}

// Global flag to track if initial validation has been done (persists across component re-mounts)
let globalHasValidated = false;

export const TimeValidationGuard = React.memo(
  ({
    children,
    college,
    candidate,
    onTimeExpired,
  }: TimeValidationGuardProps) => {
    const { offset: serverTimeOffset } = useServerTime();
    const [validation, setValidation] = useState<ValidationResult | null>(null);
    const [isValidating, setIsValidating] = useState(!globalHasValidated);

    useEffect(() => {
      const validateTiming = (isInitial = false) => {
        // Strict null check - don't proceed if any required data is missing
        if (!college || !candidate || serverTimeOffset === null) {
          if (isInitial) {
            setIsValidating(true);
          }
          return;
        }

        // Only show loading state for initial validation
        if (isInitial && !globalHasValidated) {
          setIsValidating(true);
        }

        try {
          const result = validateAssessmentTiming(
            college,
            candidate,
            serverTimeOffset
          );
          setValidation(result);
        } catch (error) {
          console.error("Error validating assessment timing:", error);
          setValidation({
            isValid: false,
            message: "Unable to validate assessment timing. Please try again.",
            icon: AlertCircle,
            iconColor: "text-red-500",
            titleColor: "text-red-600",
          });
        } finally {
          if (isInitial && !globalHasValidated) {
            setIsValidating(false);
            globalHasValidated = true;
          }
        }
      };

      // Only run initial validation if we haven't done it yet globally
      if (!globalHasValidated) {
        validateTiming(true);
      } else {
        // If we've already validated globally, just run a quick validation without loading state
        validateTiming(false);
      }

      // Silent validation every 60 seconds (increased from 30s)
      const interval = setInterval(() => validateTiming(false), 60000);
      return () => clearInterval(interval);
    }, [college, candidate, serverTimeOffset]);

    useEffect(() => {
      // Check for time expiration and trigger callback
      if (
        validation?.isTimeExpired &&
        onTimeExpired &&
        !candidate?.assesment_submitted_at
      ) {
        onTimeExpired();
      }
    }, [validation, onTimeExpired, candidate]);

    // If assessment is already submitted, bypass time validation
    if (candidate?.assesment_submitted_at) {
      return <>{children}</>;
    }

    // Show loading state with strict null checks
    if (
      !college ||
      !candidate ||
      serverTimeOffset === null ||
      (!globalHasValidated && (isValidating || !validation))
    ) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <Clock className="h-12 w-12 text-orange-500 mx-auto" />
                <h3 className="text-lg font-semibold text-orange-600">
                  {serverTimeOffset === null
                    ? "Synchronizing Time"
                    : isValidating && !globalHasValidated
                    ? "Validating Assessment Time"
                    : "Loading Assessment"}
                </h3>
                <p className="text-gray-600">
                  {serverTimeOffset === null
                    ? "Connecting to server for time synchronization..."
                    : isValidating && !globalHasValidated
                    ? "Checking server time..."
                    : "Please wait while we validate your access..."}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // If we have validation results and they're invalid, show error
    if (validation && !validation.isValid) {
      const IconComponent = validation.icon;

      // If the assessment window has closed, trigger the onTimeExpired callback
      // Only trigger if the assessment has not already been submitted
      if (
        validation.isTimeExpired &&
        onTimeExpired &&
        !candidate.assesment_submitted_at
      ) {
        onTimeExpired();
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <IconComponent
                  className={`h-12 w-12 ${validation.iconColor} mx-auto`}
                />
                <h3
                  className={`text-lg font-semibold ${validation.titleColor}`}
                >
                  Assessment Not Available
                </h3>
                <p className="text-gray-600">{validation.message}</p>

                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Assessment Hours (IST):</strong>{" "}
                    {formatDisplayTime(college.start_time)} - {formatDisplayTime(college.end_time)}
                  </p>
                  {college.college_visit_date && (
                    <p className="text-sm text-gray-700">
                      <strong>Date:</strong>{" "}
                      {formatDisplayDate(college.college_visit_date)}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    All times are in Indian Standard Time (IST)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return <>{children}</>;
  }
);

TimeValidationGuard.displayName = "TimeValidationGuard";
